import {
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
  reactErrorHandler,
  withScope,
} from "@sentry/react";
import type { ErrorInfo } from "react";
import { datadogRum } from "@datadog/browser-rum";

import { UserFacingError } from "src/lib/user-facing-error";
import { GraphQlError, HttpError } from "src/utils/fetcher";

export { getCurrentScope } from "@sentry/react";

const FILTERED_ERROR_MESSAGES = [
  "Failed to fetch",
  "Domain could not be extracted from email of guest",
];

export { UserFacingError } from "src/lib/user-facing-error";

const shouldFilterError = (error: unknown): boolean => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  return FILTERED_ERROR_MESSAGES.some((filtered) =>
    errorMessage.includes(filtered),
  );
};

/**
 * react-query's cancelQueries() rejects with a { silent: true, revert: true } object.
 * This is normal control flow, not an actual error.
 */
export const isReactQueryCancellation = (error: unknown): boolean =>
  error != null && typeof error === "object" && "silent" in error;

// Chrome, Firefox and Safari wording, in that order.
const NETWORK_ERROR_MESSAGES = [
  "Failed to fetch",
  "NetworkError when attempting to fetch resource",
  "Load failed",
];

/** fetch() rejects with a TypeError when the request never gets a response; the message check keeps our own TypeErrors out. */
export const isNetworkError = (error: unknown): boolean =>
  error instanceof TypeError &&
  NETWORK_ERROR_MESSAGES.some((message) => error.message.includes(message));

const isPermissionDeniedError = (error: unknown): boolean =>
  error instanceof GraphQlError && error.extensions.code === "FORBIDDEN";

/**
 * Backend validation failures (Apollo `UserInputError`, e.g. journey graph and
 * activation checks) arrive over the wire as a `GraphQlError` with this code.
 * They are expected outcomes of user input, not bugs — the UI already surfaces
 * them inline. Classify by wire code because a server-thrown error can never be
 * an `instanceof UserFacingError`.
 */
export const isValidationError = (error: unknown): boolean =>
  error instanceof GraphQlError && error.extensions.code === "BAD_USER_INPUT";

// Session state after a workspace switch elsewhere, not a bug; WorkspaceValidation already offers a reload.
export const isWorkspaceMismatchError = (error: unknown): boolean => {
  if (!(error instanceof HttpError) || error.code !== 409) {
    return false;
  }
  const session = error.body?.details?.session;
  return typeof session === "object" && session !== null;
};

export const isUserFacingError = (error: unknown): error is UserFacingError =>
  error instanceof UserFacingError;

const DEFINITE_CHUNK_LOAD_PATTERNS = [
  "Failed to fetch dynamically imported module", // Chrome
  "error loading dynamically imported module", // Firefox
  "Importing a module script failed", // Safari — module import failure
  "is not a valid JavaScript MIME type", // Safari — stale chunk served as HTML
  "Unable to preload CSS", // Vite — stale CSS chunk hash after deploy
];

const AMBIGUOUS_CHUNK_LOAD_PATTERNS = [
  "Load failed", // Safari — also matches regular fetch() failures
];

const ALL_CHUNK_LOAD_PATTERNS = [
  ...DEFINITE_CHUNK_LOAD_PATTERNS,
  ...AMBIGUOUS_CHUNK_LOAD_PATTERNS,
];

/** Matches all known chunk-load error patterns. Used for Sentry fingerprinting. */
export const isChunkLoadError = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message : String(error);
  return ALL_CHUNK_LOAD_PATTERNS.some((pattern) => msg.includes(pattern));
};

/**
 * Matches only unambiguous chunk-load patterns. Used by the unhandledrejection
 * fallback so Safari fetch() failures don't trigger unnecessary reloads.
 * The ambiguous "Load failed" pattern is handled by vite:preloadError instead.
 */
export const isDefiniteChunkLoadError = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message : String(error);
  return DEFINITE_CHUNK_LOAD_PATTERNS.some((pattern) => msg.includes(pattern));
};

export const trackPermissionDenied = (context: {
  source: "graphql_forbidden" | "hasura_null";
  operation?: string;
}) => {
  datadogRum.addAction("rbac:denied", context);
};

export const captureException = (
  error: Parameters<typeof sentryCaptureException>[0],
  sentryOptions?: Parameters<typeof sentryCaptureException>[1],
  datadogOptions?: {
    source?: string;
    component?: string;
    errorType?: string;
  },
) => {
  // Categorize chunk-load before the broad "Failed to fetch" filter; the
  // Chrome chunk-load message is a superstring of "Failed to fetch" and would
  // otherwise be swallowed before reaching the chunk-load fingerprint.
  if (isChunkLoadError(error)) {
    withScope((scope) => {
      scope.setFingerprint(["chunk-load-error"]);
      scope.setTag("errorCategory", "chunk-load");
      scope.setLevel("warning");
      sentryCaptureException(error, sentryOptions);
    });
    return;
  }

  if (shouldFilterError(error)) {
    return;
  }

  if (isReactQueryCancellation(error)) {
    return;
  }

  if (isUserFacingError(error)) {
    return;
  }

  if (isPermissionDeniedError(error)) {
    trackPermissionDenied({ source: "graphql_forbidden" });
    return;
  }

  if (isValidationError(error)) {
    return;
  }

  if (isWorkspaceMismatchError(error)) {
    return;
  }

  sentryCaptureException(error, sentryOptions);

  datadogRum.addError(error, datadogOptions);
};

export const captureMessage = (
  ...args: Parameters<typeof sentryCaptureMessage>
) => {
  sentryCaptureMessage(...args);
  datadogRum.addAction(
    args[0],
    typeof args[1] === "object" && "extra" in args[1]
      ? args[1].extra
      : undefined,
  );
};

export const captureReactErrorBoundaryError = (
  error: Error,
  errorInfo: ErrorInfo,
) => {
  // Chunk-load detection must run before the broad "Failed to fetch" filter.
  if (isChunkLoadError(error)) {
    withScope((scope) => {
      scope.setFingerprint(["chunk-load-error"]);
      scope.setTag("errorCategory", "chunk-load");
      scope.setLevel("warning");
      sentryCaptureException(error);
    });
    return;
  }

  if (shouldFilterError(error)) {
    return;
  }

  // reactErrorHandler wraps captureReactException which properly processes
  // the componentStack by creating a linked error with the stack trace,
  // allowing Sentry to apply source maps
  withScope((scope) => {
    scope.setTag("errorCategory", "error-boundary-crash");
    const handler = reactErrorHandler();
    handler(error, errorInfo);
  });

  datadogRum.addError(error, {
    source: "error-boundary",
    errorType: "javascript",
  });
};
